import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import Immutable from 'immutable';
import { FormattedMessage, useIntl } from 'react-intl';

import Context from '@bibliocommons/context';
import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';
import { catalogBibShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { Dropdown, DropdownMenuHeader, DropdownMenuItem, DropdownMenuDivider } from '@bibliocommons/base-dropdown';

import RedirectActions from 'app/actions/RedirectActions';
import FullScreenOverlayActions from 'app/actions/FullScreenOverlayActions';
import { BibBriefContainer } from 'app/components/bibs/shared/BibBrief';
import { FullScreenOverlayContainer } from 'app/components/shared/FullScreenOverlay';
import AddToListOverlay from 'app/components/shared/AddToListOverlay';
import { AllCommunityTagsContainer } from 'app/components/bibs/BibPage/Explore/CommunityTags/AllCommunityTags';
import { UI_COMPONENT_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { convertMetadataIdToLegacyId } from 'app/helpers/bib/bibHelpers';

import './ManageContributions.scss';

export default function ManageContributions({ catalogBib, trackAddToListClick, trackAddToNewListClick }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { mobileApp, features } = useContext(Context);
  const [listOverlayOpen, setListOverlayOpen] = useState(false);
  const [tagsOverlayOpen, setTagsOverlayOpen] = useState(false);
  const legacyId = convertMetadataIdToLegacyId(catalogBib.get('metadataId'));
  const listOverlayKey = 'add-to-list-overlay-transact-panel';
  const tagsOverlayKey = 'tags-overlay-transact-panel';

  const addToNewListLabel = intl.formatMessage({ id: 'add_to_list_new' });

  function openOverlay(overlayKey, type) {
    if (type === 'list') setListOverlayOpen(true);
    if (type === 'tags') setTagsOverlayOpen(true);
    dispatch(FullScreenOverlayActions.openOverlay(overlayKey, { authRequired: true }));
  }

  function closeOverlay(overlayKey, type) {
    if (type === 'list') setListOverlayOpen(false);
    if (type === 'tags') setTagsOverlayOpen(false);
    dispatch(FullScreenOverlayActions.closeOverlay(overlayKey));
  }

  function handleAddToNewList() {
    const uiEntity = {
      ui_component_label: addToNewListLabel,
      ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT
    };
    trackAddToNewListClick({ uiEntity });
    dispatch(RedirectActions.redirect(`/user_lists/new?bib=${legacyId}`, { authRequired: true }));
  }

  function renderNewListMenuItem() {
    return (
      <DropdownMenuItem
        handleClick={handleAddToNewList}
        item={Immutable.Map({
          label: addToNewListLabel,
          value: 'add-to-new-list'
        })}
      />
    );
  }

  function renderExistingListMenuItem() {
    const label = (
      <>
        <span aria-hidden>
          <FormattedMessage id="add_to_list_existing" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="sr_add_to_list_existing" />
        </ScreenReaderMessage>
      </>
    );
    return (
      <DropdownMenuItem
        handleClick={() => openOverlay(listOverlayKey, 'list')}
        item={Immutable.Map({
          label,
          value: 'add-to-existing-list'
        })}
      />
    );
  }

  function renderTagsMenuItem() {
    if (features.get(TOGGLZ_FEATURES.ENABLE_BIB_PAGE_STAFF_TAGGING)) {
      return (
        <>
          <DropdownMenuDivider />
          <DropdownMenuItem
            handleClick={() => openOverlay(tagsOverlayKey, 'tags')}
            item={Immutable.Map({
              label: <FormattedMessage id="add_and_remove_tags" />,
              value: 'add_and_remove_tags'
            })}
          />
        </>
      );
    }
    return null;
  }

  function renderOverlay() {
    if (listOverlayOpen) {
      const bibTitle = catalogBib.getIn(['brief', 'title']);
      const metadataId = catalogBib.get('metadataId');

      const renderBibBrief = () => {
        return <BibBriefContainer metadataId={metadataId} />;
      };

      return (
        <FullScreenOverlayContainer
          dataKey="add-to-list-overlay"
          overlayKey={listOverlayKey}
          handleClose={() => closeOverlay(listOverlayKey, 'list')}
        >
          <AddToListOverlay
            bibTitle={bibTitle}
            metadataId={metadataId}
            renderBibBrief={renderBibBrief}
            trackAddToListClick={trackAddToListClick}
          />
        </FullScreenOverlayContainer>
      );
    }
    if (tagsOverlayOpen) {
      return (
        <FullScreenOverlayContainer
          dataKey="add-tags-overlay"
          overlayKey={tagsOverlayKey}
          handleClose={() => closeOverlay(tagsOverlayKey, 'tags')}
        >
          <AllCommunityTagsContainer metadataId={catalogBib.get('metadataId')} />
        </FullScreenOverlayContainer>
      );
    }
    return null;
  }

  return mobileApp ? null : (
    <div className="cp-manage-contributions">
      <Dropdown
        block
        dataKey="manage-contributions-dropdown"
        renderLabel={() => <FormattedMessage id="manage_contributions" />}
      >
        <DropdownMenuHeader>
          <FormattedMessage id="add_to_list" />
        </DropdownMenuHeader>
        {renderNewListMenuItem()}
        {renderExistingListMenuItem()}
        {renderTagsMenuItem()}
      </Dropdown>
      {renderOverlay()}
    </div>
  );
}

ManageContributions.propTypes = {
  catalogBib: catalogBibShape.isRequired,
  trackAddToListClick: PropTypes.func.isRequired,
  trackAddToNewListClick: PropTypes.func.isRequired
};
